import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useToast } from './ui/use-toast';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Play,
  Square,
  Pause,
  MapPin,
  Clock,
  Navigation,
  AlertCircle,
  CheckCircle,
  FileText,
  ArrowLeft,
  Camera,
  User,
  MessageSquare,
  Bell,
  X,
  Image as ImageIcon,
  Activity
} from 'lucide-react';

const RegistroViagemPWA = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Estados principais
  const [viagemAtiva, setViagemAtiva] = useState(null);
  const [status, setStatus] = useState('inativo'); // inativo, em_andamento, pausada
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState('');
  
  // Dados da viagem em tempo real
  const [kmRodados, setKmRodados] = useState(0);
  const [tempoDecorrido, setTempoDecorrido] = useState(0);
  const [tempoPausas, setTempoPausas] = useState(0);
  
  // Histórico
  const [viagens, setViagens] = useState([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  
  // FASE 2 - Visitas
  const [visitaAtiva, setVisitaAtiva] = useState(null);
  const [nomeCliente, setNomeCliente] = useState('');
  const [observacoesVisita, setObservacoesVisita] = useState('');
  const [fotosVisita, setFotosVisita] = useState([]);
  const [mostrarModalVisita, setMostrarModalVisita] = useState(false);
  const [mostrarModalObservacao, setMostrarModalObservacao] = useState(false);
  const [textoObservacao, setTextoObservacao] = useState('');
  
  // FASE 2 - Alertas
  const [alertas, setAlertas] = useState([]);
  const [mostrarAlertas, setMostrarAlertas] = useState(false);
  
  // DEBUG - Estados para monitoramento
  const [pontosGPSEnviados, setPontosGPSEnviados] = useState(0);
  const [ultimoPontoEnviado, setUltimoPontoEnviado] = useState(null);
  const [errosGPS, setErrosGPS] = useState([]);
  
  // Refs
  const watchIdRef = useRef(null);
  const intervaloEnvioRef = useRef(null);
  const intervaloTempoRef = useRef(null);
  const intervaloAlertasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Espelham o state para quem roda "preso" a um render antigo (callback do watchPosition,
  // setInterval): sem isso eles enxergam sempre viagemAtiva=null e status='inativo' e
  // nenhum ponto de GPS chegava a ser enviado.
  const viagemAtivaRef = useRef(null);
  const statusRef = useRef('inativo');
  const iniciandoRef = useRef(false);
  const [iniciando, setIniciando] = useState(false);

  useEffect(() => {
    viagemAtivaRef.current = viagemAtiva;
    statusRef.current = status;
  }, [viagemAtiva, status]);

  // Tempo em pausas ao vivo: total das pausas já fechadas (vem do servidor) + a pausa em curso.
  const pausasFechadasMinRef = useRef(0);
  const pausaInicioRef = useRef(null);

  const calcularTempoPausas = () => {
    const emCurso = pausaInicioRef.current
      ? Math.max(0, Math.floor((Date.now() - pausaInicioRef.current) / 60000))
      : 0;
    return pausasFechadasMinRef.current + emCurso;
  };

  // Sincroniza com a viagem vinda do servidor (pausas fechadas + início da pausa em curso)
  const aplicarPausasDaViagem = (viagem) => {
    pausasFechadasMinRef.current = viagem.total_pausas_minutos || 0;
    const ultima = (viagem.pausas || []).slice(-1)[0];
    pausaInicioRef.current = viagem.status === 'pausada' && ultima && !ultima.hora_retomada
      ? new Date(ultima.hora_pausa).getTime()
      : null;
    setTempoPausas(calcularTempoPausas());
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Obter localização GPS
  const obterLocalizacao = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocalização não suportada'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            velocidade: position.coords.speed ? position.coords.speed * 3.6 : null // m/s para km/h
          };
          setLocation(loc);
          setLocationError('');
          resolve(loc);
        },
        (error) => {
          let errorMsg = 'Erro ao obter localização';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMsg = 'Permissão de localização negada';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMsg = 'Localização indisponível';
              break;
            case error.TIMEOUT:
              errorMsg = 'Timeout ao obter localização';
              break;
            default:
              errorMsg = 'Erro desconhecido';
          }
          setLocationError(errorMsg);
          reject(new Error(errorMsg));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  // Iniciar rastreamento GPS contínuo
  const iniciarRastreamento = () => {
    if (watchIdRef.current) {
      console.log('⚠️ Rastreamento já está ativo');
      return;
    }

    let ultimoEnvio = Date.now();
    const INTERVALO_MINIMO = 10000; // Enviar no mínimo a cada 10 segundos

    console.log('🚀 Iniciando rastreamento GPS...');
    
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const loc = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          velocidade: position.coords.speed ? position.coords.speed * 3.6 : null
        };
        setLocation(loc);
        
        console.log('📡 GPS atualizado:', loc.latitude.toFixed(6), loc.longitude.toFixed(6), 
                    'Accuracy:', Math.round(loc.accuracy) + 'm',
                    'Speed:', loc.velocidade ? loc.velocidade.toFixed(1) + 'km/h' : 'N/A');
        
        // Enviar ponto para o backend apenas se viagem estiver ativa
        // E respeitando intervalo mínimo para não sobrecarregar
        const agora = Date.now();
        const tempoDesdeUltimoEnvio = (agora - ultimoEnvio) / 1000;
        
        if (!viagemAtivaRef.current) {
          console.log('⏸️ Viagem não ativa, não enviando ponto');
          return;
        }
        
        // Na pausa o deslocamento continua sendo gravado (marcado como is_pausa), igual ao app
        if (statusRef.current !== 'em_andamento' && statusRef.current !== 'pausada') {
          console.log('⏸️ Status não é em_andamento/pausada (atual:', statusRef.current + '), não enviando ponto');
          return;
        }
        
        if ((agora - ultimoEnvio) < INTERVALO_MINIMO) {
          console.log('⏳ Aguardando intervalo mínimo:', tempoDesdeUltimoEnvio.toFixed(1) + 's de 10s');
          return;
        }
        
        // Computador (localização por Wi-Fi/IP) costuma ter 80-100 m de erro: esses pontos
        // criavam "saltos" e inflavam o km quando a página ficava aberta durante uma viagem.
        if (loc.accuracy > 50) {
          console.log('⏸️ Precisão baixa (' + Math.round(loc.accuracy) + 'm), ponto não enviado');
          return;
        }

        // Tudo OK, enviar ponto!
        console.log('✅ Enviando ponto GPS para backend...');
        enviarPontoGPS(loc);
        ultimoEnvio = agora;
      },
      (error) => {
        console.warn('❌ Erro no rastreamento GPS:', error.message || error);
        setLocationError('Erro ao rastrear localização: ' + error.message);
        setErrosGPS(prev => [...prev.slice(-4), {
          timestamp: new Date(),
          error: 'GPS Error: ' + error.message
        }]);
      },
      {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 5000
      }
    );
    
    console.log('✅ Rastreamento GPS iniciado com sucesso');
  };

  // Parar rastreamento GPS
  const pararRastreamento = () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  };

  // Enviar ponto GPS para o backend
  const enviarPontoGPS = async (loc) => {
    // Lê a viagem pela ref (esta função é chamada de dentro do watchPosition, preso a um render antigo)
    const viagemAtiva = viagemAtivaRef.current;
    if (!viagemAtiva) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/viagens/${viagemAtiva.id}/trajeto`,
        {
          latitude: loc.latitude,
          longitude: loc.longitude,
          accuracy: loc.accuracy,
          velocidade: loc.velocidade,
          timestamp: new Date().toISOString(),
          is_pausa: statusRef.current === 'pausada',
          origem: 'web'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // DEBUG: Registrar envio bem-sucedido
      setPontosGPSEnviados(prev => prev + 1);
      setUltimoPontoEnviado(new Date());
      console.log('✅ Ponto GPS enviado com sucesso:', response.data);
      
      // Atualizar KM após enviar ponto
      await atualizarDadosViagem();
    } catch (error) {
      console.error('❌ Erro ao enviar ponto GPS:', error);
      // DEBUG: Registrar erro
      setErrosGPS(prev => [...prev.slice(-4), {
        timestamp: new Date(),
        error: error.response?.data?.detail || error.message
      }]);
    }
  };

  // Atualizar dados da viagem
  const atualizarDadosViagem = async () => {
    const viagemAtiva = viagemAtivaRef.current;
    if (!viagemAtiva) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/viagens/${viagemAtiva.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const viagemAtualizada = response.data;
      setKmRodados(viagemAtualizada.km_rodados || 0);
      aplicarPausasDaViagem(viagemAtualizada);
    } catch (error) {
      console.error('Erro ao atualizar dados da viagem:', error);
    }
  };

  // Iniciar viagem
  // Contador de tempo calculado a partir do início da viagem. O antigo "+1 a cada minuto"
  // atrasava quando o navegador reduzia os timers de uma aba em segundo plano.
  const iniciarContadorTempo = () => {
    if (intervaloTempoRef.current) {
      clearInterval(intervaloTempoRef.current);
    }
    const atualizar = () => {
      const inicio = viagemAtivaRef.current?.data_hora_inicio;
      if (!inicio) return;
      setTempoDecorrido(Math.max(0, Math.floor((Date.now() - new Date(inicio).getTime()) / 60000)));
      setTempoPausas(calcularTempoPausas());
    };
    intervaloTempoRef.current = setInterval(atualizar, 30000);
  };

  const iniciarViagem = async () => {
    // Duplo clique ou toque repetido: um pedido por vez, e só se não houver viagem aberta
    if (iniciandoRef.current || viagemAtivaRef.current) return;
    iniciandoRef.current = true;
    setIniciando(true);

    try {
      const loc = await obterLocalizacao();
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/viagens/iniciar`,
        {
          funcionario_id: user.employee_id,
          latitude: loc.latitude,
          longitude: loc.longitude,
          endereco: null,
          origem: 'web'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Atualiza as refs já: o rastreamento GPS começa logo abaixo, antes do próximo render
      viagemAtivaRef.current = response.data;
      statusRef.current = 'em_andamento';
      setViagemAtiva(response.data);
      setStatus('em_andamento');
      setKmRodados(0);
      setTempoDecorrido(0);
      pausasFechadasMinRef.current = 0;
      pausaInicioRef.current = null;
      setTempoPausas(0);
      
      // Iniciar rastreamento GPS
      iniciarRastreamento();
      
      // Iniciar contador de tempo
      iniciarContadorTempo();
      
      // Nota: Pontos GPS são enviados automaticamente pelo watchPosition
      // conforme a localização muda, com intervalo mínimo de 10 segundos
      
      toast({
        title: "Viagem Iniciada",
        description: "Rastreamento GPS ativado - KMs sendo calculados",
      });
      
    } catch (error) {
      console.error('Erro ao iniciar viagem:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Erro ao iniciar viagem",
        variant: "destructive"
      });
    } finally {
      iniciandoRef.current = false;
      setIniciando(false);
    }
  };

  // Pausar viagem
  const pausarViagem = async () => {
    try {
      let loc;
      try {
        loc = await obterLocalizacao();
      } catch (locError) {
        console.warn('Erro ao obter localização, usando última conhecida:', locError.message || locError);
        loc = location || { latitude: 0, longitude: 0, accuracy: 999 };
      }
      
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/viagens/${viagemAtiva.id}/pausar`,
        {
          latitude: loc.latitude,
          longitude: loc.longitude,
          motivo: 'Almoço/Pausa'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Refs já: o watchPosition (preso a um render antigo) passa a enviar pontos como pausa
      statusRef.current = 'pausada';
      pausaInicioRef.current = Date.now();
      setStatus('pausada');

      // O rastreamento GPS continua na pausa (pontos marcados como is_pausa)
      iniciarRastreamento();

      // O contador de tempo continua na pausa: "tempo" é o total desde o início
      // (as pausas aparecem à parte), igual ao app. Parar aqui congelava o valor.
      
      // GPS é controlado pelo watchPosition, não por interval
      
      toast({
        title: "Viagem Pausada",
        description: "Rastreamento GPS pausado",
      });
      
    } catch (error) {
      console.error('Erro ao pausar viagem:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Erro ao pausar viagem",
        variant: "destructive"
      });
    }
  };

  // Retomar viagem
  const retomarViagem = async () => {
    try {
      let loc;
      try {
        loc = await obterLocalizacao();
      } catch (locError) {
        console.warn('Erro ao obter localização, usando última conhecida:', locError.message || locError);
        loc = location || { latitude: 0, longitude: 0, accuracy: 999 };
      }
      
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/viagens/${viagemAtiva.id}/retomar`,
        {
          latitude: loc.latitude,
          longitude: loc.longitude
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      statusRef.current = 'em_andamento';
      setStatus('em_andamento');

      // O rastreamento já está ativo desde a pausa; iniciarRastreamento ignora se já existe
      iniciarRastreamento();
      
      // Retomar contador de tempo
      iniciarContadorTempo();
      
      // Nota: Pontos GPS são enviados automaticamente pelo watchPosition
      // conforme a localização muda
      
      // Atualizar dados
      await atualizarDadosViagem();
      
      toast({
        title: "Viagem Retomada",
        description: "Rastreamento GPS reativado",
      });
      
    } catch (error) {
      console.error('Erro ao retomar viagem:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Erro ao retomar viagem",
        variant: "destructive"
      });
    }
  };

  // Encerrar viagem
  const encerrarViagem = async () => {
    if (!confirm('Deseja realmente encerrar esta viagem?')) return;
    
    try {
      console.log('Tentando obter localização para encerrar viagem...');
      let loc;
      
      try {
        loc = await obterLocalizacao();
        console.log('Localização obtida:', loc);
      } catch (locError) {
        console.warn('Erro ao obter localização, usando última conhecida:', locError.message || locError);
        // Se falhar, usar a última localização conhecida ou coordenadas padrão
        loc = location || { latitude: 0, longitude: 0, accuracy: 999 };
      }
      
      const token = localStorage.getItem('token');
      console.log('Encerrando viagem:', viagemAtiva.id);
      
      const response = await axios.post(
        `${API_URL}/api/viagens/${viagemAtiva.id}/encerrar`,
        {
          latitude: loc.latitude,
          longitude: loc.longitude,
          endereco: null,
          observacao_final: null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('Viagem encerrada com sucesso:', response.data);

      // O servidor deixa a viagem em "sincronizando" até receber a confirmação. O web envia os
      // pontos na hora (não há fila local), então confirma em seguida. Sem isso a viagem nunca
      // virava "finalizada" e dependia do encerramento forçado do admin.
      try {
        await axios.post(
          `${API_URL}/api/viagens/${viagemAtiva.id}/confirmar-encerrar`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (confirmError) {
        console.error('Erro ao confirmar encerramento:', confirmError);
        toast({
          title: "Atenção",
          description: "Viagem encerrada, mas a confirmação não foi concluída. Ela aparecerá como 'sincronizando' até ser confirmada.",
          variant: "destructive"
        });
      }
      
      // Limpar intervalos e rastreamento
      pararRastreamento();
      if (intervaloEnvioRef.current) clearInterval(intervaloEnvioRef.current);
      if (intervaloTempoRef.current) clearInterval(intervaloTempoRef.current);
      if (intervaloAlertasRef.current) clearInterval(intervaloAlertasRef.current);
      
      setViagemAtiva(null);
      setStatus('inativo');
      
      toast({
        title: "Viagem Encerrada",
        description: `Total: ${response.data.km_rodados} KM rodados`,
      });
      
      // Recarregar histórico
      carregarHistorico();
      
    } catch (error) {
      console.error('Erro completo ao encerrar viagem:', error);
      console.error('Detalhes do erro:', error.response?.data);
      
      const errorMsg = error.response?.data?.detail || error.message || "Erro ao encerrar viagem";
      
      toast({
        title: "Erro",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };

  // Carregar histórico de viagens
  const carregarHistorico = async () => {
    setCarregandoHistorico(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/viagens?funcionario_id=${user.employee_id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setViagens(response.data);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    } finally {
      setCarregandoHistorico(false);
    }
  };


  // ========== FASE 2 - FUNCIONALIDADES AVANÇADAS ==========
  
  // Iniciar visita em cliente
  const iniciarVisita = async () => {
    if (!nomeCliente.trim()) {
      toast({
        title: "Atenção",
        description: "Informe o nome do cliente",
        variant: "destructive"
      });
      return;
    }
    
    try {
      console.log('Tentando obter localização para iniciar visita...');
      let loc;
      
      try {
        loc = await obterLocalizacao();
        console.log('Localização obtida:', loc);
      } catch (locError) {
        console.warn('Erro ao obter localização, usando última conhecida:', locError.message || locError);
        loc = location || { latitude: 0, longitude: 0, accuracy: 999 };
      }
      
      const token = localStorage.getItem('token');
      console.log('Iniciando visita:', nomeCliente);
      
      const response = await axios.post(
        `${API_URL}/api/viagens/${viagemAtiva.id}/visita/iniciar`,
        {
          nome_cliente: nomeCliente,
          latitude: loc.latitude,
          longitude: loc.longitude,
          observacoes: observacoesVisita
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('Visita iniciada com sucesso:', response.data);
      
      setVisitaAtiva({
        id: response.data.visita_id,
        nome_cliente: nomeCliente,
        hora_chegada: new Date()
      });
      setFotosVisita([]);
      setMostrarModalVisita(false);
      setNomeCliente('');
      setObservacoesVisita('');
      
      toast({
        title: "Visita Iniciada",
        description: `Cliente: ${nomeCliente}`,
      });
      
    } catch (error) {
      console.error('Erro completo ao iniciar visita:', error);
      console.error('Detalhes do erro:', error.response?.data);
      
      const errorMsg = error.response?.data?.detail || error.message || "Erro ao iniciar visita";
      
      toast({
        title: "Erro",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };
  
  // Finalizar visita
  const finalizarVisita = async () => {
    if (!visitaAtiva) return;
    
    try {
      console.log('Finalizando visita:', visitaAtiva.id);
      
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/viagens/${viagemAtiva.id}/visita/finalizar`,
        {
          visita_id: visitaAtiva.id
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('Visita finalizada com sucesso');
      
      toast({
        title: "Visita Finalizada",
        description: `Cliente: ${visitaAtiva.nome_cliente}`,
      });
      
      setVisitaAtiva(null);
      setFotosVisita([]);
      
    } catch (error) {
      console.error('Erro completo ao finalizar visita:', error);
      console.error('Detalhes do erro:', error.response?.data);
      
      const errorMsg = error.response?.data?.detail || error.message || "Erro ao finalizar visita";
      
      toast({
        title: "Erro",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };
  
  // Tirar/adicionar foto à visita
  const adicionarFotoVisita = async (event) => {
    if (!visitaAtiva) {
      toast({
        title: "Atenção",
        description: "Inicie uma visita primeiro",
        variant: "destructive"
      });
      return;
    }
    
    if (fotosVisita.length >= 3) {
      toast({
        title: "Limite Atingido",
        description: "Máximo de 3 fotos por visita",
        variant: "destructive"
      });
      return;
    }
    
    const file = event.target.files[0];
    if (!file) return;
    
    // Converter para base64
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${API_URL}/api/viagens/${viagemAtiva.id}/visita/foto`,
          {
            visita_id: visitaAtiva.id,
            foto_base64: reader.result
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        setFotosVisita([...fotosVisita, reader.result]);
        
        toast({
          title: "Foto Adicionada",
          description: `${fotosVisita.length + 1}/3 fotos`,
        });
        
      } catch (error) {
        console.error('Erro ao adicionar foto:', error);
        toast({
          title: "Erro",
          description: error.response?.data?.detail || "Erro ao adicionar foto",
          variant: "destructive"
        });
      }
    };
    reader.readAsDataURL(file);
  };
  
  // Adicionar observação
  const adicionarObservacao = async () => {
    if (!textoObservacao.trim()) {
      toast({
        title: "Atenção",
        description: "Digite uma observação",
        variant: "destructive"
      });
      return;
    }
    
    try {
      console.log('Adicionando observação...');
      let loc;
      
      try {
        loc = await obterLocalizacao();
        console.log('Localização obtida:', loc);
      } catch (locError) {
        console.warn('Erro ao obter localização, usando última conhecida:', locError.message || locError);
        loc = location || { latitude: 0, longitude: 0, accuracy: 999 };
      }
      
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/viagens/${viagemAtiva.id}/observacao`,
        {
          latitude: loc.latitude,
          longitude: loc.longitude,
          observacao: textoObservacao
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('Observação adicionada com sucesso');
      
      toast({
        title: "Observação Adicionada",
        description: "Registrado com sucesso",
      });
      
      setTextoObservacao('');
      setMostrarModalObservacao(false);
      
    } catch (error) {
      console.error('Erro completo ao adicionar observação:', error);
      console.error('Detalhes do erro:', error.response?.data);
      
      const errorMsg = error.response?.data?.detail || error.message || "Erro ao adicionar observação";
      
      toast({
        title: "Erro",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };
  
  // Verificar alertas (parada longa)
  const verificarAlertas = async () => {
    if (!viagemAtiva || status !== 'em_andamento') return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/viagens/${viagemAtiva.id}/alertas`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.alertas && response.data.alertas.length > 0) {
        setAlertas(response.data.alertas);
        setMostrarAlertas(true);
        
        // Tentar enviar notificação push
        if ('Notification' in window && Notification.permission === 'granted') {
          response.data.alertas.forEach(alerta => {
            new Notification('⚠️ Alerta - Registro de Viagem', {
              body: alerta.mensagem,
              icon: '/logo192.png'
            });
          });
        }
      }
    } catch (error) {
      console.error('Erro ao verificar alertas:', error);
    }
  };
  
  // Solicitar permissão para notificações
  const solicitarPermissaoNotificacao = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        toast({
          title: "Notificações Ativadas",
          description: "Você receberá alertas de parada longa",
        });
      }
    }
  };
  
  // ========== END FASE 2 ==========


  // Verificar se há viagem em andamento ao carregar componente
  useEffect(() => {
    const verificarViagemAtiva = async () => {
      try {
        const token = localStorage.getItem('token');
        // Buscar viagens em andamento OU pausadas
        const response = await axios.get(
          `${API_URL}/api/viagens?funcionario_id=${user.employee_id}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        // Filtrar viagens ativas (em_andamento ou pausada)
        const viagensAtivas = response.data.filter(v => 
          v.status === 'em_andamento' || v.status === 'pausada'
        );
        
        if (viagensAtivas && viagensAtivas.length > 0) {
          const viagem = viagensAtivas[0];
          // Refs primeiro: o rastreamento GPS começa logo abaixo, antes do próximo render
          viagemAtivaRef.current = viagem;
          statusRef.current = viagem.status;
          setViagemAtiva(viagem);
          setStatus(viagem.status);
          setKmRodados(viagem.km_rodados || 0);
          
          // Calcular tempo decorrido
          const inicio = new Date(viagem.data_hora_inicio);
          const agora = new Date();
          const minutos = Math.floor((agora - inicio) / 60000);
          setTempoDecorrido(minutos);
          aplicarPausasDaViagem(viagem);

          // Em andamento ou pausada: o rastreamento roda nos dois casos
          // (na pausa os pontos vão marcados como is_pausa)
          iniciarRastreamento();
          iniciarContadorTempo();
          // O envio dos pontos é feito pelo próprio watchPosition (a cada 10 s no mínimo).
        }
      } catch (error) {
        console.error('Erro ao verificar viagem ativa:', error);
      }
    };
    
    verificarViagemAtiva();
    carregarHistorico();
    solicitarPermissaoNotificacao();
    
    // FASE 2: Verificar alertas a cada 5 minutos
    intervaloAlertasRef.current = setInterval(() => {
      verificarAlertas();
    }, 300000); // 5 minutos
    
    // Cleanup ao desmontar
    return () => {
      pararRastreamento();
      if (intervaloEnvioRef.current) clearInterval(intervaloEnvioRef.current);
      if (intervaloTempoRef.current) clearInterval(intervaloTempoRef.current);
      if (intervaloAlertasRef.current) clearInterval(intervaloAlertasRef.current);
    };
  }, []);

  // Formatar tempo em HH:MM
  const formatarTempo = (minutos) => {
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;
    return `${String(horas).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Formatar data
  const formatarData = (dataISO) => {
    const data = new Date(dataISO);
    return data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-4 pb-20">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Voltar"
            >
              <ArrowLeft className="h-6 w-6 text-gray-700" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">📍 Registro de Viagem</h1>
              <p className="text-gray-600 text-sm mt-1">Controle de quilometragem</p>
            </div>
          </div>
          {status !== 'inativo' && (
            <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold animate-pulse">
              ATIVA EM SEGUNDO PLANO
            </div>
          )}
        </div>
      </div>

      {/* Status GPS */}
      {status !== 'inativo' && (
        <Card className="mb-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Navigation className="h-5 w-5" />
                <span className="font-semibold">Status GPS</span>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                location ? 'bg-green-500' : 'bg-yellow-500'
              }`}>
                {location ? `Precisão: ${Math.round(location.accuracy)}m` : 'Aguardando...'}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* DEBUG Panel - Monitoramento em Tempo Real */}
      {status !== 'inativo' && viagemAtiva && (
        <Card className="mb-4 border-2 border-purple-300 bg-purple-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-purple-900 flex items-center space-x-2">
              <Activity className="h-4 w-4" />
              <span>🔍 Monitor GPS (Debug)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-white p-2 rounded">
                <div className="text-gray-600">Pontos Enviados</div>
                <div className="text-lg font-bold text-purple-600">{pontosGPSEnviados}</div>
              </div>
              <div className="bg-white p-2 rounded">
                <div className="text-gray-600">Último Envio</div>
                <div className="text-lg font-bold text-purple-600">
                  {ultimoPontoEnviado 
                    ? `há ${Math.floor((Date.now() - ultimoPontoEnviado.getTime()) / 1000)}s`
                    : 'Nenhum'
                  }
                </div>
              </div>
            </div>
            
            {location && (
              <div className="bg-white p-2 rounded text-xs">
                <div className="text-gray-600 mb-1">Posição Atual:</div>
                <div className="font-mono text-purple-700">
                  {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                  {location.velocidade && ` | ${location.velocidade.toFixed(0)} km/h`}
                </div>
              </div>
            )}
            
            {errosGPS.length > 0 && (
              <div className="bg-red-50 p-2 rounded border border-red-200">
                <div className="text-red-700 text-xs font-semibold mb-1">⚠️ Erros Recentes:</div>
                {errosGPS.slice(-2).map((erro, idx) => (
                  <div key={idx} className="text-xs text-red-600 truncate">
                    • {erro.error}
                  </div>
                ))}
              </div>
            )}
            
            {status === 'em_andamento' && pontosGPSEnviados === 0 && (
              <div className="bg-yellow-50 p-2 rounded border border-yellow-300 text-xs text-yellow-800">
                ⚠️ Nenhum ponto GPS enviado ainda. Verifique se você está em movimento!
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Viagem Ativa */}
      {status !== 'inativo' && viagemAtiva && (
        <Card className="mb-4">
          <CardHeader className={`${
            status === 'em_andamento' ? 'bg-green-50' : 'bg-yellow-50'
          } border-b`}>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {status === 'em_andamento' ? (
                  <>
                    <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-green-800">EM VIAGEM</span>
                  </>
                ) : (
                  <>
                    <Pause className="h-5 w-5 text-yellow-600" />
                    <span className="text-yellow-800">PAUSADA</span>
                  </>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-6">
            {/* Dados em tempo real */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-3xl font-bold text-blue-600">{kmRodados.toFixed(1)}</div>
                <div className="text-sm text-gray-600 mt-1">KM Rodados</div>
              </div>
              
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-3xl font-bold text-green-600">{formatarTempo(tempoDecorrido)}</div>
                <div className="text-sm text-gray-600 mt-1">Tempo Total</div>
              </div>
              
              {(tempoPausas > 0 || status === 'pausada') && (
                <div className="col-span-2 text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{formatarTempo(tempoPausas)}</div>
                  <div className="text-sm text-gray-600 mt-1">Tempo em Pausas</div>
                </div>
              )}
            </div>

            {/* Botões de controle */}
            <div className="space-y-3">
              {status === 'em_andamento' && (
                <>
                  {/* FASE 2: Botões de Visita e Observação */}
                  <div className="grid grid-cols-2 gap-2">
                    {!visitaAtiva ? (
                      <Button
                        onClick={() => setMostrarModalVisita(true)}
                        className="bg-blue-600 hover:bg-blue-700"
                        size="sm"
                      >
                        <User className="h-4 w-4 mr-1" />
                        Visita
                      </Button>
                    ) : (
                      <Button
                        onClick={finalizarVisita}
                        className="bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Fim Visita
                      </Button>
                    )}
                    
                    <Button
                      onClick={() => setMostrarModalObservacao(true)}
                      variant="outline"
                      size="sm"
                    >
                      <MessageSquare className="h-4 w-4 mr-1" />
                      Observação
                    </Button>
                  </div>
                  
                  <Button
                    onClick={pausarViagem}
                    className="w-full bg-yellow-500 hover:bg-yellow-600"
                    size="lg"
                  >
                    <Pause className="h-5 w-5 mr-2" />
                    Pausar Viagem
                  </Button>
                  
                  <Button
                    onClick={encerrarViagem}
                    className="w-full bg-red-600 hover:bg-red-700"
                    size="lg"
                  >
                    <Square className="h-5 w-5 mr-2" />
                    Encerrar Viagem
                  </Button>
                </>
              )}
              
              {status === 'pausada' && (
                <>
                  <Button
                    onClick={retomarViagem}
                    className="w-full bg-green-600 hover:bg-green-700"
                    size="lg"
                  >
                    <Play className="h-5 w-5 mr-2" />
                    Retomar Viagem
                  </Button>
                  
                  <Button
                    onClick={encerrarViagem}
                    className="w-full bg-red-600 hover:bg-red-700"
                    size="lg"
                  >
                    <Square className="h-5 w-5 mr-2" />
                    Encerrar Viagem
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Iniciar Nova Viagem */}
      {status === 'inativo' && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="text-center space-y-4">
              <div className="mx-auto w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                <MapPin className="h-10 w-10 text-blue-600" />
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Pronto para viajar?</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Inicie uma nova viagem para começar o rastreamento de quilometragem
                </p>
              </div>

              {locationError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{locationError}</p>
                </div>
              )}
              
              <Button
                onClick={iniciarViagem}
                disabled={iniciando}
                className="w-full bg-green-600 hover:bg-green-700"
                size="lg"
              >
                <Play className="h-5 w-5 mr-2" />
                {iniciando ? 'Iniciando...' : 'Iniciar Nova Viagem'}
              </Button>

              {/* Botão de Emergência */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center mb-2">
                  Viagem travada? Use a ferramenta de emergência:
                </p>
                <Button
                  onClick={async () => {
                    if (!window.confirm('Tem certeza? Isso encerrará TODAS as viagens ativas/pausadas!')) {
                      return;
                    }
                    try {
                      const token = localStorage.getItem('token');
                      const response = await axios.post(
                        `${API_URL}/api/viagens/forcar-encerramento/${user.employee_id}`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` }}
                      );
                      toast({
                        title: "✅ Sucesso",
                        description: response.data.message,
                      });
                      // Recarregar página após 2 segundos
                      setTimeout(() => window.location.reload(), 2000);
                    } catch (error) {
                      toast({
                        title: "Erro",
                        description: error.response?.data?.detail || "Erro ao encerrar viagens",
                        variant: "destructive"
                      });
                    }
                  }}
                  variant="outline"
                  className="w-full border-red-300 text-red-700 hover:bg-red-50"
                  size="sm"
                >
                  🔧 Encerrar Viagens Travadas
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Histórico de Viagens */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Histórico de Viagens</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {carregandoHistorico ? (
            <div className="text-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-600 mt-2">Carregando...</p>
            </div>
          ) : viagens.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nenhuma viagem registrada ainda
            </div>
          ) : (
            <div className="space-y-3">
              {viagens.slice(0, 10).map((viagem) => (
                <div
                  key={viagem.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-semibold">
                        {formatarData(viagem.data_hora_inicio)}
                      </span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      viagem.status === 'aprovada' ? 'bg-green-100 text-green-700' :
                      viagem.status === 'finalizada' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {viagem.status === 'aprovada' ? 'Aprovada' :
                       viagem.status === 'finalizada' ? 'Finalizada' : viagem.status}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">KM:</span>
                      <span className="font-bold ml-1">{viagem.km_rodados.toFixed(1)} KM</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Tempo:</span>
                      <span className="font-bold ml-1">{formatarTempo(viagem.tempo_total_minutos || 0)}</span>
                    </div>
                  </div>
                  
                  {viagem.status === 'aprovada' && (
                    <div className="mt-2 flex items-center space-x-1 text-xs text-green-600">
                      <CheckCircle className="h-3 w-3" />
                      <span>Aprovada em {formatarData(viagem.data_aprovacao)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* FASE 2: Modal de Iniciar Visita */}
      {mostrarModalVisita && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Registrar Visita</h3>
              <button onClick={() => setMostrarModalVisita(false)}>
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Nome do Cliente</label>
                <input
                  type="text"
                  value={nomeCliente}
                  onChange={(e) => setNomeCliente(e.target.value)}
                  placeholder="Digite o nome do cliente"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Observações (opcional)</label>
                <textarea
                  value={observacoesVisita}
                  onChange={(e) => setObservacoesVisita(e.target.value)}
                  placeholder="Ex: Reunião de alinhamento"
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              
              <Button
                onClick={iniciarVisita}
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={!nomeCliente.trim()}
              >
                <User className="h-5 w-5 mr-2" />
                Iniciar Visita
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* FASE 2: Card de Visita Ativa */}
      {visitaAtiva && (
        <Card className="fixed bottom-20 left-4 right-4 z-40 shadow-2xl">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-semibold text-blue-600">Visita em Andamento</div>
                <div className="text-lg font-bold">{visitaAtiva.nome_cliente}</div>
              </div>
              <Button
                onClick={finalizarVisita}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
              >
                Finalizar
              </Button>
            </div>
            
            {/* Fotos da visita */}
            <div className="flex items-center space-x-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={adicionarFotoVisita}
                className="hidden"
              />
              
              <Button
                onClick={() => fileInputRef.current?.click()}
                size="sm"
                variant="outline"
                disabled={fotosVisita.length >= 3}
              >
                <Camera className="h-4 w-4 mr-1" />
                Foto ({fotosVisita.length}/3)
              </Button>
              
              {fotosVisita.map((foto, index) => (
                <div key={index} className="w-12 h-12 rounded border overflow-hidden">
                  <img src={foto} alt={`Foto ${index + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* FASE 2: Modal de Observação */}
      {mostrarModalObservacao && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Adicionar Observação</h3>
              <button onClick={() => setMostrarModalObservacao(false)}>
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <textarea
                value={textoObservacao}
                onChange={(e) => setTextoObservacao(e.target.value)}
                placeholder="Ex: Desvio por obra na via, trânsito intenso..."
                rows={4}
                className="w-full border rounded-lg px-3 py-2"
              />
              
              <Button
                onClick={adicionarObservacao}
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={!textoObservacao.trim()}
              >
                <MessageSquare className="h-5 w-5 mr-2" />
                Salvar Observação
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* FASE 2: Alertas de Parada Longa */}
      {mostrarAlertas && alertas.length > 0 && (
        <div className="fixed top-20 left-4 right-4 z-50">
          {alertas.map((alerta, index) => (
            <div
              key={index}
              className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-2 shadow-lg"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <Bell className="h-6 w-6 text-yellow-600 mt-1 animate-pulse" />
                  <div>
                    <div className="font-bold text-yellow-900">⚠️ Alerta</div>
                    <div className="text-sm text-yellow-800 mt-1">{alerta.mensagem}</div>
                  </div>
                </div>
                <button
                  onClick={() => setMostrarAlertas(false)}
                  className="text-yellow-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RegistroViagemPWA;
